import React from 'react';
import { motion } from 'framer-motion';
import { Safe, Caduceus, Injection, ICON_SIZES } from './icons';
import { useWhyChooseSection } from '../hooks/useWhyChooseSection';
import { getCurrentLocale } from '../services/api';

function WhyChooseSection(): React.JSX.Element {
  const { data, loading, error } = useWhyChooseSection();
  const locale = getCurrentLocale();

  // Debug logging for why choose section data
  if (data && !loading && !error) {
    console.log('🎯 WhyChooseSection - ✅ Strapi data loaded:', !!data);
    console.log('🎯 WhyChooseSection - Data keys:', Object.keys(data || {}));
    console.log('🎯 WhyChooseSection - Title:', data.title);
    console.log('🎯 WhyChooseSection - Features count:', data.features?.length || 0);
    console.log('🎯 WhyChooseSection - Background color:', data.backgroundColor);
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  // Default fallback features
  const defaultFeatures = [
    {
      icon: 'safe' as const,
      title: "Medisch verantwoord",
      description: "Behandelingen op basis van wetenschap en trichologie."
    },
    {
      icon: 'caduceus' as const,
      title: "Volledige discretie",
      description: "Geen wachtruimte, volle privacy, behandeling in rust."
    },
    {
      icon: 'injection' as const,
      title: "Toegankelijke methode",
      description: "Zonder operatie, past in je agenda en je leven."
    }
  ];

  // Get icon component based on icon string
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'safe': return Safe;
      case 'caduceus': return Caduceus; 
      case 'injection': return Injection;
      default: return Safe;
    }
  };

  // Locale-aware safe defaults
  const DEFAULT_NL = {
    title: 'Waarom kiezen voor Hairmedics?',
    features: defaultFeatures,
    backgroundColor: '#FFFFFF',
  };

  // Basic Arabic detection
  const hasArabic = (text: string | undefined | null): boolean => {
    if (!text) return false;
    return /[\u0600-\u06FF]/.test(text);
  };

  // Safely derive values and avoid cross-locale bleed
  const sectionData = data || DEFAULT_NL;
  let features = data?.features || defaultFeatures;
  let backgroundColor = data?.backgroundColor || '#FFFFFF';

  // If nl but incoming text appears Arabic, fall back to NL defaults
  if (locale === 'nl') {
    const titleLooksAr = hasArabic(sectionData?.title);
    const anyFeatureLooksAr = features.some(f => hasArabic(f?.title) || hasArabic(f?.description));
    if (titleLooksAr || anyFeatureLooksAr) {
      features = defaultFeatures;
      backgroundColor = '#FFFFFF';
    }
  }

  if (loading) {
    return (
      <section 
        className="why-choose section-padding"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="why-choose-content">
          <div className="loading-placeholder">
            <div className="loading-title"></div>
            <div className="loading-text"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <motion.section
        className="why-choose section-padding"
        style={{ backgroundColor: "#FFFFFF" }}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="why-choose-content">
          <motion.h2 className="section-title text-center" variants={itemVariants}>
            Waarom kiezen voor Hairmedics?
          </motion.h2>
          
          <div className="features-grid">
            {defaultFeatures.map((feature, index) => {
              const IconComponent = getIconComponent(feature.icon);
              return (
                <motion.div className="feature-card" key={index} variants={itemVariants}>
                  <div className="feature-icon-large">
                    <IconComponent size={ICON_SIZES.xxl} className="text-primary" />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      className="why-choose section-padding"
      style={{ backgroundColor }}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="why-choose-content">
        <motion.h2 className="section-title text-center" variants={itemVariants}>
          {sectionData.title}
        </motion.h2>
        
        {/* Subtitle removed per request */}
        
        <div className="features-grid">
          {features.map((feature, index) => {
            const IconComponent = getIconComponent(feature.icon);
            return (
              <motion.div className="feature-card" key={feature.id || index} variants={itemVariants}>
                <div className="feature-icon-large">
                  <IconComponent size={ICON_SIZES.xxl} className="text-primary" />
                </div>
                <h3 className="why-choose-title">{feature.title}</h3>
                <p className="why-choose-description">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
}

export default WhyChooseSection;